import type { Element, ElementContent, Root, RootContent, Text } from "hast";
import type { Plugin } from "unified";
import type { TermMatcher } from "~/utils/termMatcher";
import {
  computeTermMatches,
  normalizeTermKey,
  TERM_HIGHLIGHT_CLASS,
} from "~/utils/termMatching";
import { hasMdastOrigin } from "./provenance";

/**
 * Options for the term-highlight HAST pass.
 * `regex` matches AI-extracted terms; `matcher` is the medication Aho-Corasick
 * matcher. Both are optional — the pass is a no-op when neither is provided.
 */
export interface RehypeTermHighlightOptions {
  regex: RegExp | null;
  matcher: TermMatcher | null;
}

/** Property name (camelCased for hast) mapping to the `data-term-key` attribute. */
const TERM_KEY_PROPERTY = "dataTermKey";

/**
 * Builds a `<mark class="term-highlight" data-term-key=… tabindex=0 role=button>`
 * element wrapping the matched text — identical to the markup v1 injects, so the
 * shared `TermDefinitionDialog` click delegation works unchanged.
 */
function createMark(value: string, term: string): Element {
  return {
    type: "element",
    tagName: "mark",
    properties: {
      className: [TERM_HIGHLIGHT_CLASS],
      [TERM_KEY_PROPERTY]: normalizeTermKey(term),
      tabIndex: 0,
      role: "button",
    },
    children: [{ type: "text", value }],
  };
}

/**
 * Splits a single text node into a sequence of text + `<mark>` nodes for every
 * matched term. Matching is per text node — matches never span sibling nodes,
 * so a term split across a `strong`/`emphasis` boundary (`heart **failure**`)
 * is not highlighted, matching v1's pre-parse string behaviour (§6.3).
 */
function highlightTextNode(
  node: Text,
  options: RehypeTermHighlightOptions
): Array<Element | Text> {
  const matches = computeTermMatches(
    node.value,
    options.regex,
    options.matcher
  );
  if (matches.length === 0) return [node];

  const result: Array<Element | Text> = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.start > cursor) {
      result.push({
        type: "text",
        value: node.value.slice(cursor, match.start),
      });
    }
    result.push(
      createMark(node.value.slice(match.start, match.end), match.term)
    );
    cursor = match.end;
  }
  if (cursor < node.value.length) {
    result.push({ type: "text", value: node.value.slice(cursor) });
  }
  return result;
}

/**
 * Rebuilds a children list, highlighting term matches in text nodes and
 * recursing into non-MDAST-originated element subtrees. Generic over the child
 * content type so it applies to both `Root` and `Element` children without any
 * unsafe cast (`Element`/`Text` outputs are members of both content unions).
 */
function highlightChildren<C extends RootContent | ElementContent>(
  children: C[],
  options: RehypeTermHighlightOptions
): Array<C | Element | Text> {
  const next: Array<C | Element | Text> = [];
  for (const child of children) {
    if (child.type === "text") {
      next.push(...highlightTextNode(child, options));
      continue;
    }
    if (child.type === "element" && !hasMdastOrigin(child)) {
      child.children = highlightChildren(child.children, options);
    }
    next.push(child);
  }
  return next;
}

/**
 * Rehype pass that highlights AI/medication terms (§6.3).
 *
 * Provenance-based, tags-only protection: subtrees of MDAST-originated
 * `code`/`inlineCode`, parsed-Markdown links, and citation anchors carry the
 * transient marker and are skipped, while term text inside raw-HTML elements
 * materialised by `rehypeRaw` (raw `<a>`, `<code>`, …) is still highlighted.
 * Runs exactly once and never descends into the `<mark>` nodes it creates, so
 * it produces no nested marks. Must run before `rehypeSourceLabels`.
 */
export const rehypeTermHighlight: Plugin<[RehypeTermHighlightOptions], Root> =
  function (options: RehypeTermHighlightOptions) {
    const active = options.regex !== null || options.matcher !== null;

    return function (tree: Root): void {
      if (!active) return;
      tree.children = highlightChildren(tree.children, options);
    };
  };
